import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paper } from "@mui/material";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "./copilot.css";

import EmptyState from "./empty-state";
import ConversationView from "./conversation-view";
import CopilotOverlay from "./copilot-overlay";

import useCopilotStore from "@/store/copilotStore";
import useUserStore from "@/store/user-store";
import { createCitations } from "@/utils/util";
import { useChatAPI } from "@/hooks/copilot/use-chat-api";
import { useFileUpload } from "@/hooks/copilot/use-file-upload";
import {
  deleteMessage,
  updateMessageResponseTime,
} from "@/api/message/message.api";
import { useChatTimerStore } from "@/store/chat-timer-store";
import { updateConversation } from "@/api/conversation/conversation.api";
import {
  ACCEPTED_FILE_TYPES,
  DEFAULT_ADDITIONAL_PROMPT,
} from "@/constants/copilot";

const Copilot = () => {
  const navigate = useNavigate();
  const [userPrompt, setUserPrompt] = useState("");
  const [fileStates, setFileStates] = useState([]);
  const [showDragOverlay, setShowDragOverlay] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsError, setAttachmentsError] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState("Knowledge Base");
  const [stopReason, setStopReason] = useState("stop_sequence");
  const [fileError, setFileError] = useState("");
  const [additionalPromptOpen, setAdditionalPromptOpen] = useState(false);
  const {
    systemPrompt,
    newChatSystemPrompt,
    setSystemPrompt,
    setNewChatSystemPrompt,
  } = useCopilotStore();

  const [searchParams] = useSearchParams();
  const conversationIdFromSearch = searchParams.get("conversationId");

  useEffect(() => {
    if (!conversationIdFromSearch) {
      setSystemPrompt(DEFAULT_ADDITIONAL_PROMPT);
    }
  }, [conversationIdFromSearch]);

  

  

  const fileInputRef = useRef(null);
  const controllerRef = useRef(null);
  const messagesEndRef = useRef(null);

  

  const userStore = useUserStore();
  const copilotStore = useCopilotStore();
  
  const { startGeneration, stopGeneration, elapsedTime } = useChatTimerStore();
  const conversationType = searchParams.get("convType");
  const [hasSetTheResponseTime, setHasSetTheResponseTime] = useState(false);

  const handlePromptChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserPrompt(event?.currentTarget?.value ?? "");
  };

  const scrollToBottom = () => {
    if (messagesEndRef?.current)
      (messagesEndRef.current as any)?.scrollIntoView({ behavior: "smooth" });
  };

  const { uploadAttachment } = useFileUpload(setAttachments, setFileStates);

  const handleFileChange = useCallback(
    (event: any) => {
      const files = Array.from(event.target.files);
      const validFiles = files.filter((file: any) =>
        ACCEPTED_FILE_TYPES.includes(file.type)
      );

      validFiles.forEach((file: any, index) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFileStates(
              (prev) => [...prev, { file, preview: e.target?.result }] as any
            );
          };
          reader.readAsDataURL(file);
        } else {
          setFileStates((prev) => [...prev, { file, preview: null }] as any);
        }
        uploadAttachment(file, index + fileStates.length);
      });

      if (validFiles.length !== files.length) {
        console.log(
          "Some files were not accepted. Please upload .pdf, .xlsx, .jpg, .png, .xls, or .xlsx files."
        );
      }

      if (files?.length > 3) {
        setAttachmentsError("Maximum of three files allowed");
      }
    },
    [setFileStates, uploadAttachment]
  );

  const handleDrop = useCallback(
    (event: any) => {
      event.preventDefault();

      const files = Array.from(event.dataTransfer.files);

      if (fileStates.length + files.length > 3) {
        setFileError("Maximum of three files allowed");
        copilotStore.setFileError("Maximum of three files allowed");
        setShowDragOverlay(false);
        return;
      }

      setFileError("");
      copilotStore.setFileError("");

      handleFileChange({
        target: { files: event.dataTransfer.files },
      });
      setShowDragOverlay(false);
    },
    [handleFileChange]
  );

  const handleFileSelect = (event: any) => {
    const input = event.target;
    const files = Array.from(input.files);
    const successfulFileStates = fileStates.filter(
      (fs: any) => fs.uploaded === true
    );

    input.value = "";
    const existingFiles = new Set(fileStates.map((fs: any) => fs.file.name));
    const uniqueFiles = files.filter(
      (file: any) => !existingFiles.has(file.name)
    );

    if (successfulFileStates.length + files.length > 3) {
      setFileError("Maximum of three files allowed");
      copilotStore.setFileError("Maximum of three files allowed");
      return;
    }

    setFileError("");
    copilotStore.setFileError("");

    const newFileStates = uniqueFiles.map((file: any) => {
      return {
        file,
        preview: file.type.startsWith("image/") ? null : null,
        progress: 0,
      };
    });

    setFileStates((prevStates) => [...prevStates, ...newFileStates] as any);

    uniqueFiles.forEach((file, index) => {
      uploadAttachment(file, index + fileStates.length);
    });
  };

  const getTools = (source: string) => {
    if (source === "Web") return ["web_available"];
    if (source === "Knowledge Base and Web")
      return ["knowledge_available", "web_available"];
    if (source === "Knowledge Base") return ["knowledge_available"];
    return [];
  };

  const { sendChatMessage, stopChatMessage, messageId } = useChatAPI({
    controllerRef,
    setChatLoading: copilotStore.setChatLoading,
    setStopReason,
    copilotStore,
  });

  const sendUserQuery = async (
    userQuery = "",
    files = [],
    systemPromptContent = systemPrompt
  ) => {
    try {
      const isNewConversation = !copilotStore.currentConversationId;
      const userMessage = userQuery || userPrompt;
      if (!userMessage || copilotStore.chatLoading) return;
      if (fileStates?.filter((f: any) => !f.doc_url || f.error)?.length > 0) {
        setFileStates(() => []);
        return;
      }

      copilotStore.setChatLoading(true);
      console.log("chatLoading before API call:", chatLoading);

      const additionalFiles = files?.length
        ? files
            ?.filter((f: any) => f.doc_url && !f.error)
            .map((f: any) => f.doc_url)
        : fileStates
            ?.filter((f: any) => f.doc_url && !f.error)
            .map((f: any) => f.doc_url);

      copilotStore.addMessage({
        message_type: "User",
        attachments: {
          additional_files: additionalFiles,
        },
        data: { content: userMessage },
      });

      setUserPrompt("");
      setAttachments([]);
      setFileStates([]);

      const userInfo = JSON.parse(localStorage?.getItem("user_data") ?? "{}");
      const uuid = userInfo.id;
      const toolList = getTools(selectedSource);
      const section = conversationType === "compare" ? "compare_files" : "";
      const process = conversationType === "compare" ? "resume" : "copilot";

      const data = {
        attachments: {
          additional_files: additionalFiles,
        },
        source_ids: [],
        project_name: "",
        conversation_id: copilotStore.currentConversationId ?? "",
        uuid: copilotStore.uuid,
        display_message: false,
        message: userMessage,
        user_id: uuid,
        section: section,
        process: process,
        version_uri: "",
        additional_prompt: systemPromptContent,
        tool_list: toolList,
        system_prompt: systemPromptContent,
      };
      startGeneration();
      const onNewConversation = (conversationId) => {
        if (isNewConversation) {
          setNewChatSystemPrompt(DEFAULT_ADDITIONAL_PROMPT);
          sessionStorage.removeItem("newChatSystemPrompt");
        }
      };
      await sendChatMessage(data, onNewConversation);
      stopGeneration();
      copilotStore.setResponseTime(messageId, elapsedTime);
      copilotStore.setChatLoading(false);

      
      
    } catch (error: any) {
      stopGeneration();
      copilotStore.setChatLoading(false);
    }
  };

  useEffect(() => {
    const saveResponseTime = async () => {
      if (messageId && elapsedTime) {
        const payload = {
          message_id: messageId,
          response_time: elapsedTime,
        };
        const res = await updateMessageResponseTime(payload);
        if (res?.success) {
          setHasSetTheResponseTime(() => true);
        }
      }
    };
    saveResponseTime();
  }, [messageId]);

  const handleSendUserQuery = () => {
    const isNewConversation = !copilotStore.currentConversationId;
    const systemPromptToUse = isNewConversation
      ? newChatSystemPrompt
      : systemPrompt;
    sendUserQuery(userPrompt, fileStates, systemPromptToUse);
  };

  const stopChat = async () => {
    setStopReason("user_canceled");
    setChatLoading(false);
    await stopChatMessage();
  };

  const performRegeneration = async () => {
    const messages = copilotStore?.messages || [];
    const lastUserMessageIndex = messages.length - 2;

    if (lastUserMessageIndex < 0) return;

    const userMessage = messages[lastUserMessageIndex]?.data?.content || "";
    setUserPrompt(userMessage);
    setFileStates(
      (
        messages[lastUserMessageIndex] as any
      )?.attachments.additional_files?.map((u: any) => {
        return { progress: 100, uploaded: true, doc_url: u };
      })
    );
    const files = (
      messages[lastUserMessageIndex] as any
    )?.attachments.additional_files?.map((u: any) => {
      return { progress: 100, uploaded: true, doc_url: u };
    });

    const lastMessageId = messages[messages.length - 1]?.id;
    const userMessageId = messages[lastUserMessageIndex]?.id;

    if (lastMessageId && userMessageId) {
      await Promise.all([
        deleteMessage(lastMessageId, { is_deleted: true }),
        deleteMessage(userMessageId, { is_deleted: true }),
      ]);
    }

    copilotStore?.popMessage();
    await sendUserQuery(userMessage, files);
  };

  const updateFeedback = async (
    rating: any,
    feedback: any,
    message_id: any
  ) => {
    setFeedbackOpen(false);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const data = {
        message_id: message_id,
        feedback: rating,
        feedback_details: {
          details: feedback ?? "",
          issue_type: "Response feedback",
        },
      };

      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/message`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        console.error("Feedback submit failed:", response.status);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleEditUserMessage = async (messageId: any, newContent: any) => {
    const messages = copilotStore?.messages || [];
    const parentId = copilotStore?.parentId || "";
    const userMessageIndex = messages.findIndex((msg) => msg.id === messageId);

    if (userMessageIndex < 0)
      return;

    const assistantMessageIndex = userMessageIndex + 1;
    const assistantMessage = messages[assistantMessageIndex];
    const userMessage = messages[userMessageIndex];

    const files =
      (messages[userMessageIndex] as any)?.attachments?.additional_files?.map(
        (u: any) => {
          return { progress: 100, uploaded: true, doc_url: u };
        }
      ) ?? [];

    if (assistantMessage && assistantMessage.id && messageId) {
      await Promise.all([
        deleteMessage(assistantMessage.id, { is_deleted: true }),
        deleteMessage(messageId, { is_deleted: true }),
      ]);

      copilotStore?.popMessage();
    } else {
      if (userMessage?.id) {
        await deleteMessage(userMessage?.id, { is_deleted: true });
      } else if (parentId && parentId !== "") {
        await deleteMessage(parentId, { is_deleted: true });
        copilotStore?.setParentId("");
      }
      copilotStore?.sliceMessage();
    }

    await sendUserQuery(newContent, files);
  };

  useEffect(() => {
    const down = (e: any) => {
      if (e.key === "Enter" && !e.shiftKey && !additionalPromptOpen) {
        e.preventDefault();
        handleSendUserQuery();
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [userPrompt, attachments, additionalPromptOpen]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchConversation = async () => {
      const currentConversationId = copilotStore.currentConversationId;
      if (!currentConversationId) {
        return;
      }

      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/messages?conversation_id=${currentConversationId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          }
        );

        copilotStore?.clearMessages();
        response?.data?.data
          ?.filter((msg: any) => msg.data?.content !== "<Output>")
          .map((msg: any) => {
            copilotStore.addMessage({
              ...msg,
              sources: createCitations(msg?.data?.SourceFiles),
            });
          });
      } catch (err) {
        if (!controller?.signal?.aborted) {
          console.error("Error fetching conversation:", err);
        }
      }
    };
    if ((copilotStore.currentConversationId || hasSetTheResponseTime) && !copilotStore.chatLoading) {
      fetchConversation();
      setHasSetTheResponseTime(() => false);
    }
    return () => {
      controller.abort();
    };
  }, [copilotStore.currentConversationId, hasSetTheResponseTime]);

  useEffect(() => {
    if (conversationIdFromSearch) {
      copilotStore.setCurrentConversationId(conversationIdFromSearch);
      const fetchConversationDetails = async () => {
        try {
          const accessToken = localStorage.getItem("accessToken");
          const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/conversation/${conversationIdFromSearch}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (response.data?.data?.system_prompt) {
            setSystemPrompt(response.data.data.system_prompt);
          } else {
            setSystemPrompt(DEFAULT_ADDITIONAL_PROMPT);
          }
        } catch (error) {
          console.error("Failed to fetch conversation details:", error);
        }
      };
      fetchConversationDetails();
    } else {
      copilotStore.resetConversation();
      copilotStore.clearMessages();
    }
    return () => {
      if (controllerRef?.current) {
        (controllerRef.current as any).abort();
      }
      copilotStore.setChatLoading(false);
    };
  }, [conversationIdFromSearch]);

  

  useEffect(() => {
    return () => {
      if (controllerRef?.current) {
        (controllerRef.current as any).abort();
      }
      copilotStore.setChatLoading(false);
    };
  }, [copilotStore.uuid]);

  const handleSystemPromptChange = (newPrompt: any) => {
    const isNewConversation = !copilotStore.currentConversationId;
    if (isNewConversation) {
      setNewChatSystemPrompt(newPrompt);
    } else {
      setSystemPrompt(newPrompt);
    }
  };

  const handleSystemPromptUpdate = async (newPrompt: any) => {
    const isNewConversation = !copilotStore.currentConversationId;
    if (isNewConversation) {
      setNewChatSystemPrompt(newPrompt);
    } else {
      setSystemPrompt(newPrompt);
      if (conversationIdFromSearch) {
        try {
          await updateConversation(conversationIdFromSearch, {
            system_prompt: newPrompt,
          });
        } catch (error) {
          console.error("Failed to update conversation:", error);
        }
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [copilotStore.messages, copilotStore.chatLoading]);

  return (
    <Paper
      onDragOver={(e) => {
        e.preventDefault();
        setShowDragOverlay(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setShowDragOverlay(false);
      }}
      onDrop={handleDrop}
      className={`copilot-main min-h-20 bg-gray-100 rounded-md ${showDragOverlay
          ? "bg-blend-overlay bg-gray-300 opacity-60 bg-[url(/assets/google.png)] z-0"
          : ""
      }`}
    >
      {showDragOverlay && <CopilotOverlay />}

      {!copilotStore?.messages?.length ? (
        <EmptyState
          setUserPrompt={setUserPrompt}
          userPrompt={userPrompt}
          handlePromptChange={handlePromptChange}
          fileStates={fileStates}
          handleSendUserQuery={handleSendUserQuery}

          selectedSource={selectedSource}
          setSelectedSource={setSelectedSource}
          chatLoading={chatLoading}
          stopChat={stopChat}
          fileInputRef={fileInputRef}
          handleFileSelect={handleFileSelect}
          setFileStates={setFileStates}
          setFileError={setFileError}
          systemPrompt={newChatSystemPrompt}
          setSystemPrompt={handleSystemPromptChange}
          handleSystemPromptUpdate={handleSystemPromptUpdate}
          additionalPromptOpen={additionalPromptOpen}
          setAdditionalPromptOpen={setAdditionalPromptOpen}
        />
      ) : (
        <ConversationView
          copilotStore={copilotStore}
          userStore={userStore}
          chatLoading={copilotStore.chatLoading}
          performRegeneration={performRegeneration}
          updateFeedback={updateFeedback}
          userPrompt={userPrompt}
          handlePromptChange={handlePromptChange}
          setUserPrompt={setUserPrompt}
          fileStates={fileStates}
          handleSendUserQuery={handleSendUserQuery}

          selectedSource={selectedSource}
          setSelectedSource={setSelectedSource}
          stopChat={stopChat}
          fileInputRef={fileInputRef}
          handleFileSelect={handleFileSelect}
          setFileStates={setFileStates}
          messagesEndRef={messagesEndRef}
          fileError={fileError}
          setFileError={setFileError}
          conversationType={conversationType}
          responseTime={elapsedTime}
          handleEditUserMessage={handleEditUserMessage}
          stopReason={stopReason}
          systemPrompt={systemPrompt}
          setSystemPrompt={setSystemPrompt}
          handleSystemPromptUpdate={handleSystemPromptUpdate}
          additionalPromptOpen={additionalPromptOpen}
          setAdditionalPromptOpen={setAdditionalPromptOpen}
          conversationId={copilotStore.currentConversationId}
        />
      )}
      <ToastContainer />
    </Paper>
  );
};

export default Copilot;
